import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, MessageCircle, CalendarClock, AlertTriangle } from "lucide-react";
import { useApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, Loading, Btn } from "../components/kit";
import { AuthFile } from "../components/order/AuthFile";
import { WhatsAppPanel } from "../components/order/WhatsAppPanel";
import { DeleteOrderButton } from "../components/order/DeleteOrderButton";
import { StatusPanel, AssignPanel, ProductionPanel, PaymentPanel, HistoryPanel } from "../components/order/Panels";
import { deadlineState, fmtDate, rupiah, num, waLink } from "../lib/constants";

const BANNER = {
  late: ["bg-rose-600 text-white", "Order ini sudah melewati deadline"],
  soon: ["bg-amber-400 text-amber-950", "Deadline order ini tinggal ≤ 2 hari"],
};

function ItemsCard({ o }) {
  return (
    <Card className="space-y-3" data-testid="items-card">
      <h3 className="font-bold text-lg">Detail Pesanan</h3>
      {o.items.map((it, i) => (
        <div key={`${it.cup_type}-${it.cup_size}-${i}`} className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-2" data-testid={`detail-item-${i}`}>
          <div className="flex justify-between gap-2"><span className="font-bold">{it.cup_type} {it.cup_size}</span><span className="font-mono font-bold">{num(it.qty)} pcs</span></div>
          <div className="text-sm text-slate-600">{it.colors} warna · {it.print_side || "-"} · {rupiah(it.unit_price)}/pcs</div>
          <div className="text-sm"><span className="text-emerald-700 font-semibold">Jadi {num(it.good_qty)}</span> · <span className="text-rose-600 font-semibold">Reject {num(it.reject_qty)}</span></div>
          {it.files?.length > 0 && <div className="flex gap-2 overflow-x-auto pb-1">{it.files.map((f, j) => <AuthFile key={f.path} file={f} testid={`item-${i}-file-${j}`} />)}</div>}
        </div>
      ))}
      {o.notes && <div className="p-3 rounded-xl bg-sky-50 border border-sky-200 text-sm" data-testid="order-notes"><b>Catatan:</b> {o.notes}</div>}
    </Card>
  );
}

function DetailHeader({ o, user, sup }) {
  const st = deadlineState(o);
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <Link to="/order" className="inline-flex items-center gap-1 text-slate-600 font-semibold h-11" data-testid="back-to-orders"><ArrowLeft className="w-5 h-5" /> Daftar Order</Link>
        <div className="flex gap-2">
          {user.is_owner && <DeleteOrderButton o={o} />}
          {user.role === "admin" && <Link to={`/order/${o.id}/edit`}><Btn variant="secondary" data-testid="edit-order-btn"><Pencil className="w-4 h-4" /> Edit</Btn></Link>}
        </div>
      </div>
      {BANNER[st] && <div className={`p-3 rounded-xl font-bold flex items-center gap-2 ${BANNER[st][0]}`} data-testid="deadline-banner"><AlertTriangle className="w-5 h-5" /> {BANNER[st][1]}</div>}
      <div className="fade-up">
        <div className="font-mono font-bold text-sky-600" data-testid="detail-order-no">{o.order_no}</div>
        <h1 className="text-3xl font-extrabold tracking-tight" data-testid="detail-customer-name">{o.customer.name}</h1>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
          <span>Order: {fmtDate(o.order_date)}</span>
          <span className="inline-flex items-center gap-1 font-semibold"><CalendarClock className="w-4 h-4" /> Deadline: {fmtDate(o.deadline)}</span>
          <span>Operator: <b data-testid="detail-operator">{o.operator_name || "-"}</b></span>
          <span>Prioritas: <b>{o.priority}</b>{o.schedule_date && ` · Jadwal ${fmtDate(o.schedule_date)}`}</span>
        </div>
        {o.customer.whatsapp && sup && (
          <a href={waLink(o.customer.whatsapp)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-500 text-white font-bold" data-testid="whatsapp-link"><MessageCircle className="w-5 h-5" /> {o.customer.whatsapp}</a>
        )}
      </div>
    </>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: o, mutate } = useApi(`/orders/${id}`);
  if (!o) return <Loading />;
  const onDone = (d) => mutate(d, false);
  const sup = ["admin", "head_operator"].includes(user.role);
  return (
    <>
      <DetailHeader o={o} user={user} sup={sup} />
      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 space-y-4">
          <StatusPanel o={o} role={user.role} onDone={onDone} />
          <ProductionPanel o={o} role={user.role} onDone={onDone} />
          <ItemsCard o={o} />
        </div>
        <div className="lg:col-span-5 space-y-4">
          {user.role === "admin" && <WhatsAppPanel o={o} />}
          {sup && <AssignPanel key={o.operator_id + o.priority + o.schedule_date} o={o} onDone={onDone} />}
          <PaymentPanel o={o} role={user.role} onDone={onDone} />
          <HistoryPanel o={o} />
        </div>
      </div>
    </>
  );
}
